import React, { useState } from "react";
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  CircularProgress,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { useSelector } from "react-redux";
import axios from "axios";
import { baseURL } from "services/API";
import { useOrganisations } from "services/hooks/rentals";

const adminBase = `${baseURL}/admin`;

const Organisations = () => {
  const token = useSelector((state) => state.token);
  const role = useSelector((state) => state.role);
  const { organisations, isLoadingOrganisations, error, setNewData } = useOrganisations();
  const [message, setMessage] = useState({ type: "", text: "" });
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({
    name: "",
    code: "",
    status: true,
    username: "",
    password: "",
    first_name: "",
    last_name: "",
    email: "",
    phone_number: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const list = Array.isArray(organisations) ? organisations : [];
  const loading = isLoadingOrganisations;
  const errorMessage = error ? "Failed to load organisations" : null;

  const openCreate = () => {
    setEditingId(null);
    setForm({
      name: "",
      code: "",
      status: true,
      username: "",
      password: "",
      first_name: "",
      last_name: "",
      email: "",
      phone_number: "",
    });
    setDialogOpen(true);
  };

  const openEdit = (row) => {
    setEditingId(row.id);
    setForm({
      name: row.name || "",
      code: row.code || "",
      status: row.status !== false,
      username: "",
      password: "",
      first_name: "",
      last_name: "",
      email: "",
      phone_number: "",
    });
    setDialogOpen(true);
  };

  const handleSubmit = async () => {
    if (!form.name?.trim() || !form.code?.trim()) {
      setMessage({ type: "error", text: "Organisation name and code are required" });
      return;
    }
    if (!editingId) {
      if (!form.username?.trim() || !form.password || !form.first_name?.trim() || !form.last_name?.trim()) {
        setMessage({ type: "error", text: "Manager username, password, first name and last name are required" });
        return;
      }
    }
    setSubmitting(true);
    setMessage({ type: "", text: "" });
    const headers = { Authorization: `Bearer ${token}` };
    try {
      if (editingId) {
        await axios.patch(`${adminBase}/organisations/${editingId}`, {
          name: form.name.trim(),
          code: form.code.trim(),
          status: form.status,
        }, { headers });
        setMessage({ type: "success", text: "Organisation updated" });
      } else {
        await axios.post(`${adminBase}/organisations`, {
          name: form.name.trim(),
          code: form.code.trim(),
          username: form.username.trim(),
          password: form.password,
          first_name: form.first_name.trim(),
          last_name: form.last_name.trim(),
          email: form.email?.trim() || undefined,
          phone_number: form.phone_number?.trim() || undefined,
        }, { headers });
        setMessage({ type: "success", text: "Organisation and first manager created" });
      }
      setDialogOpen(false);
      setNewData(true);
    } catch (e) {
      const err = e?.response?.data?.error || e?.response?.data?.errors || e?.message;
      setMessage({ type: "error", text: typeof err === "object" ? JSON.stringify(err) : err });
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Soft-delete this organisation?")) return;
    const headers = { Authorization: `Bearer ${token}` };
    try {
      await axios.delete(`${adminBase}/organisations/${id}`, { headers });
      setMessage({ type: "success", text: "Organisation deactivated" });
      setNewData(true);
    } catch (e) {
      setMessage({
        type: "error",
        text: e?.response?.data?.error || e?.message || "Delete failed",
      });
    }
  };

  if (role !== "admin") {
    return (
      <Box m={2}>
        <Alert severity="warning">Only admins can access Organisations.</Alert>
      </Box>
    );
  }

  return (
    <Box m="20px">
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h4">Organisations</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
          Add Organisation
        </Button>
      </Box>

      {message.text && (
        <Alert severity={message.type === "error" ? "error" : "success"} sx={{ mb: 2 }} onClose={() => setMessage({ type: "", text: "" })}>
          {message.text}
        </Alert>
      )}

      <TableContainer component={Paper}>
        {loading ? (
          <Box p={4} display="flex" justifyContent="center">
            <CircularProgress />
          </Box>
        ) : errorMessage ? (
          <Alert severity="error">{errorMessage}</Alert>
        ) : (
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>ID</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Code</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {list.map((row) => (
                <TableRow key={row.id}>
                  <TableCell>{row.id}</TableCell>
                  <TableCell>{row.name}</TableCell>
                  <TableCell>{row.code}</TableCell>
                  <TableCell>{row.status ? "Active" : "Inactive"}</TableCell>
                  <TableCell align="right">
                    <IconButton size="small" onClick={() => openEdit(row)}>
                      <EditIcon />
                    </IconButton>
                    <IconButton size="small" color="error" onClick={() => handleDelete(row.id)}>
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </TableContainer>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingId ? "Edit Organisation" : "Create Organisation + First Manager"}</DialogTitle>
        <DialogContent>
          <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 0, mb: 1 }}>
            Organisation
          </Typography>
          <TextField
            fullWidth
            label="Organisation name"
            value={form.name}
            onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
            margin="normal"
          />
          <TextField
            fullWidth
            label="Organisation code"
            value={form.code}
            onChange={(e) => setForm((p) => ({ ...p, code: e.target.value }))}
            margin="normal"
          />
          {editingId && (
            <Box mt={2}>
              <Typography variant="body2">Status</Typography>
              <Button
                size="small"
                variant={form.status ? "contained" : "outlined"}
                onClick={() => setForm((p) => ({ ...p, status: true }))}
              >
                Active
              </Button>
              <Button
                size="small"
                variant={!form.status ? "contained" : "outlined"}
                color="secondary"
                onClick={() => setForm((p) => ({ ...p, status: false }))}
                sx={{ ml: 1 }}
              >
                Inactive
              </Button>
            </Box>
          )}

          {!editingId && (
            <>
              <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 2, mb: 1 }}>
                First manager (can log in)
              </Typography>
              <TextField
                fullWidth
                label="Username"
                value={form.username}
                onChange={(e) => setForm((p) => ({ ...p, username: e.target.value }))}
                margin="normal"
                required
              />
              <TextField
                fullWidth
                type="password"
                label="Password"
                value={form.password}
                onChange={(e) => setForm((p) => ({ ...p, password: e.target.value }))}
                margin="normal"
                required
              />
              <TextField
                fullWidth
                label="First name"
                value={form.first_name}
                onChange={(e) => setForm((p) => ({ ...p, first_name: e.target.value }))}
                margin="normal"
                required
              />
              <TextField
                fullWidth
                label="Last name"
                value={form.last_name}
                onChange={(e) => setForm((p) => ({ ...p, last_name: e.target.value }))}
                margin="normal"
                required
              />
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={form.email}
                onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))}
                margin="normal"
              />
              <TextField
                fullWidth
                label="Phone number"
                value={form.phone_number}
                onChange={(e) => setForm((p) => ({ ...p, phone_number: e.target.value }))}
                margin="normal"
              />
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSubmit} disabled={submitting}>
            {submitting ? <CircularProgress size={24} /> : editingId ? "Update" : "Create"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Organisations;
